// src/services/userPhotoServices.js
import axios from "axios";

// Compat Vite & CRA
const _base =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_URL) ||
  process.env.REACT_APP_API_URL ||
  "";

const baseUrl = _base.replace(/\/$/, "");
const usersBase = `${baseUrl}/api/users`;

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Upload/Remplace l'avatar d’un utilisateur (local sharp 400x400 cover).
 * - POST /api/users/:id/photo?format=webp|jpg|png (défaut webp)
 * - multipart/form-data, champ "file"
 * - Réponse: { ok: true, photoURL: "/uploads/..." }
 */
export const uploadUserPhoto = async (userId, file, options = {}) => {
  if (!userId) throw new Error("userId requis");
  if (!file) throw new Error("file requis");

  const { format = "webp", onUploadProgress } = options;

  const form = new FormData();
  form.append("file", file);

  try {
    const { data } = await axios.post(
      `${usersBase}/${encodeURIComponent(
        userId
      )}/photo?format=${encodeURIComponent(format)}`,
      form,
      {
        headers: {
          ...authHeaders(),
          // axios gère Content-Type multipart automatiquement
        },
        onUploadProgress,
      }
    );
    return data; // { ok:true, photoURL }
  } catch (error) {
    console.error("[uploadUserPhoto] erreur:", error);
    throw error?.response?.data || error?.message || error;
  }
};

/**
 * Définir directement une URL existante pour l’avatar (Cloudinary ou locale).
 * - PUT /api/users/:id/photo-url
 * - body: { photoURL }
 * - Réponse: { ok:true, photoURL }
 */
export const setUserPhotoUrl = async (userId, photoURL) => {
  if (!userId) throw new Error("userId requis");
  if (!photoURL) throw new Error("photoURL requis");
  const { data } = await axios.put(
    `${usersBase}/${encodeURIComponent(userId)}/photo-url`,
    { photoURL },
    { headers: { "Content-Type": "application/json", ...authHeaders() } }
  );
  return data;
};

/**
 * Recadrer l’avatar utilisateur côté serveur.
 * - POST /api/users/:id/crop
 * - body: { x, y, width, height, format?, src? }
 * - Sortie 400x400, Réponse: { ok:true, photoURL }
 */
export const cropUserPhoto = async (userId, crop = {}) => {
  if (!userId) throw new Error("userId requis");
  const { x, y, width, height, format, src } = crop;
  const nums = [x, y, width, height];
  if (nums.some((v) => typeof v !== "number" || Number.isNaN(v))) {
    throw new Error("x, y, width, height numériques requis");
  }
  const payload = { x, y, width, height };
  if (format) payload.format = format;
  if (src) payload.src = src;

  const { data } = await axios.post(
    `${usersBase}/${encodeURIComponent(userId)}/crop`,
    payload,
    { headers: { "Content-Type": "application/json", ...authHeaders() } }
  );
  return data; // { ok:true, photoURL }
};

/**
 * Supprimer l’avatar.
 * - DELETE /api/users/:id/photo
 * - Réponse: { ok:true }
 */
export const deleteUserPhoto = async (userId) => {
  if (!userId) throw new Error("userId requis");
  const { data } = await axios.delete(
    `${usersBase}/${encodeURIComponent(userId)}/photo`,
    { headers: { ...authHeaders() } }
  );
  return data;
};

/**
 * Helper pour <img src="...">
 * - /uploads/... => préfixe par baseUrl (API)
 * - sinon (https://...) => renvoie tel quel
 */
export const toAbsoluteUserPhotoUrl = (u = "") => {
  if (!u) return "";
  const s = String(u).trim();

  // Déjà absolu, data URL ou blob
  if (
    /^https?:\/\//i.test(s) ||
    s.startsWith("data:") ||
    s.startsWith("blob:")
  ) {
    return s;
  }

  // Assets front (ex: /img/...) => on laisse tel quel
  if (!s.startsWith("/uploads")) {
    return s;
  }

  // Fichiers uploadés côté API
  const base = (
    process.env.REACT_APP_API_URL ||
    process.env.REACT_APP_API_BASE ||
    ""
  ).replace(/\/$/, ""); // supprime le slash final si présent

  return base ? `${base}${s}` : s;
};
